import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useActiveTeam } from "@/hooks/useActiveTeam";
import { useAuth } from "@/hooks/useAuth";

interface TeamReassignSelectProps {
  /** Current team_id value */
  value: string | null;
  /** Called when user selects a different team */
  onChange: (teamId: string) => void;
  /** The user_id of the entity creator */
  createdBy: string | null;
  /** The current logged-in user id */
  currentUserId: string | undefined;
}

/**
 * Compact select for reassigning team on an existing entity.
 * - QM: always visible (when multi-team)
 * - Tester: only visible for own entries (created_by = self)
 * - All other roles: hidden
 *
 * The current team_id is always shown – even if the user is not a member
 * of that team (read-only display). The user can only switch to teams
 * they belong to.
 */
export function TeamReassignSelect({
  value,
  onChange,
  createdBy,
  currentUserId,
}: TeamReassignSelectProps) {
  const { t } = useTranslation();
  const { userTeams, isMultiTeam } = useActiveTeam();
  const { hasRole } = useAuth();

  const isQM = hasRole("qualitaetsmanager");
  const isTester = hasRole("tester");
  const isOwnEntry = !!currentUserId && currentUserId === createdBy;

  // QM: always (multi-team), Tester: only own entries (multi-team)
  if (!isMultiTeam || !(isQM || (isTester && isOwnEntry))) {
    return null;
  }

  // Check if current value is in user's teams
  const currentInUserTeams = value ? userTeams.some((t) => t.id === value) : false;

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1.5">
        <Users className="h-3.5 w-3.5" />
        {t("teamAssignment.reassignTeam")}
      </Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={t("teamAssignment.reassignTeamPlaceholder")} />
        </SelectTrigger>
        <SelectContent>
          {userTeams.map((team) => (
            <SelectItem key={team.id} value={team.id}>
              {team.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {currentInUserTeams || !value
          ? t("teamAssignment.reassignTeamHint")
          : t(
              "teamAssignment.currentTeamNotMember",
              "Das aktuelle Team ist nicht in Ihren Teams. Bitte wählen Sie ein neues Team.",
            )}
      </p>
    </div>
  );
}
